import { useLayoutContext } from "arkh";
import React, { Suspense, useLayoutEffect } from "react";
import { View } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import { graphql, useLazyLoadQuery } from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorRouteProp,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { KPIAnalyticsSectionSelector } from "./KPIAnalyticsSectionSelector";
import { KPIPerWeekdayAnalytics } from "./KPIPerWeekdayAnalytics";
import { KPIStoreAnalytics } from "./KPIStoreAnalytics";
import { KPIPerWeeklyHightlight } from "./KPIWeeklyHighlightAnalytics";

import { KPIAnalyticsQuery } from "./__generated__/KPIAnalyticsQuery.graphql";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";

type Props = {
  navigation: UserLoggedInNavigatorNavigationProp<"KPIAnalytics">;
  route: UserLoggedInNavigatorRouteProp<"KPIAnalytics">;
};

export const KPIAnalytics = withWebModalStack(KPIAnalyticsC);
function KPIAnalyticsC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <KPIAnalyticsComponent {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function KPIAnalyticsComponent(props: Props & { fetchKey: string }) {
  const layout = useLayoutContext();
  const data = useLazyLoadQuery<KPIAnalyticsQuery>(
    graphql`
      query KPIAnalyticsQuery($id: ID!) {
        kpi(id: $id) {
          id
          title
          activeSections {
            id
            assignedTeam {
              id
            }
          }

          ...KPIAnalyticsSectionSelector_KPI
          ...KPIStoreAnalytics_KPI
          ...KPIPerWeekdayAnalytics
          ...KPIWeeklyHighlightAnalytics
        }
      }
    `,
    {
      id: props.route.params.id,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerTitle: data.kpi?.title ?? "",
    });
  }, [data.kpi?.title, props.navigation]);

  const [selectedSectionIds, setSelectedSectionIds] = React.useState<
    string[] | undefined
  >(() => {
    if (
      props.route.params.selectedSectionIds &&
      props.route.params.selectedSectionIds.length > 0
    ) {
      return props.route.params.selectedSectionIds;
    }
    if (props.route.params.selectedTeamId) {
      const ids =
        data.kpi?.activeSections
          .filter(
            (s) => s.assignedTeam?.id === props.route.params.selectedTeamId
          )
          .map((s) => s.id) ?? [];

      if (ids?.length > 0) {
        return ids;
      }
    }

    return undefined;
  });

  return (
    <View style={{ flex: 1 }}>
      <Suspense fallback={null}>
        {data.kpi ? (
          <KPIAnalyticsSectionSelector
            selectedSectionIds={selectedSectionIds}
            setSelectedSectionIds={setSelectedSectionIds}
            kpi={data.kpi}
          />
        ) : null}
      </Suspense>
      <ScrollView contentContainerStyle={{ paddingBottom: layout.space.large }}>
        <Suspense fallback={null}>
          {data.kpi ? (
            <>
              <KPIPerWeeklyHightlight
                kpi={data.kpi}
                sectionIdsFilter={selectedSectionIds}
              />
              <KPIPerWeekdayAnalytics
                kpi={data.kpi}
                sectionIdsFilter={selectedSectionIds}
              />
              <KPIStoreAnalytics
                kpi={data.kpi}
                sectionIdsFilter={selectedSectionIds}
              />
            </>
          ) : null}
        </Suspense>
      </ScrollView>
    </View>
  );
}
